import type { AnsweredTrial, CurvePoint, Fingerprint, Trial } from './types';
import { seededRandom } from './rng';

const DELAYS = [1, 7, 30, 90, 180, 365];
const PROBES_PER_DELAY = 3;
const LATER_AMOUNT = 100;
const PROMPTS = [
  'meteor now / moon vault later',
  'spark now / orchard later',
  'bright coin now / quiet treasury later',
  'sugar comet now / harvest moon later',
  'tiny dragon now / sleeping giant later',
  'instant lantern / delayed lighthouse'
];

function jitterMidpoint(low: number, high: number, random: () => number) {
  const mid = (low + high) / 2;
  const span = high - low;
  const jitter = (random() - 0.5) * span * 0.18;
  return Math.max(1, Math.min(LATER_AMOUNT - 1, Math.round(mid + jitter)));
}

export function createTrials(seedLabel = String(Date.now())): Trial[] {
  const random = seededRandom(seedLabel);
  const order = DELAYS.flatMap((delayDays) =>
    Array.from({ length: PROBES_PER_DELAY }, (_, probe) => ({ delayDays, probe }))
  ).sort(() => random() - 0.5);

  const bounds = new Map<number, { low: number; high: number }>();
  for (const delay of DELAYS) bounds.set(delay, { low: 1, high: LATER_AMOUNT });

  return order.map(({ delayDays, probe }, index) => {
    const bound = bounds.get(delayDays)!;
    // Vary the initial offers, but keep later probes locally updated by the store.
    const soonerAmount = jitterMidpoint(bound.low, bound.high, random);
    return {
      id: `${delayDays}-${probe}-${index}-${Math.round(random() * 1_000_000)}`,
      delayDays,
      laterAmount: LATER_AMOUNT,
      soonerDelayDays: 0,
      laterDelayDays: delayDays,
      soonerAmount,
      low: bound.low,
      high: bound.high,
      probe,
      promptTheme: PROMPTS[index % PROMPTS.length]
    };
  });
}

export function updateTrialBounds(
  trials: Trial[],
  answered: AnsweredTrial[],
  current: Trial,
  choice: 'sooner' | 'later'
): Trial[] {
  const byDelay = new Map<number, { low: number; high: number }>();
  for (const delay of DELAYS) byDelay.set(delay, { low: 1, high: LATER_AMOUNT });
  for (const a of answered) {
    const b = byDelay.get(a.delayDays)!;
    if (a.choice === 'later') b.low = Math.max(b.low, a.soonerAmount);
    else b.high = Math.min(b.high, a.soonerAmount);
  }
  const b = byDelay.get(current.delayDays)!;
  if (choice === 'later') b.low = Math.max(b.low, current.soonerAmount);
  else b.high = Math.min(b.high, current.soonerAmount);

  let random = seededRandom(`${current.id}:${choice}`);
  return trials.map((trial) => {
    const bounds = byDelay.get(trial.delayDays)!;
    if (answered.some((a) => a.id === trial.id) || trial.id === current.id) return trial;
    const isFutureSameDelay = trial.delayDays === current.delayDays;
    return isFutureSameDelay
      ? { ...trial, low: bounds.low, high: bounds.high, soonerAmount: jitterMidpoint(bounds.low, bounds.high, random) }
      : trial;
  });
}

export function estimateSubjectiveValue(low: number, high: number, choice: 'sooner' | 'later', amount: number) {
  const nextLow = choice === 'later' ? Math.max(low, amount) : low;
  const nextHigh = choice === 'sooner' ? Math.min(high, amount) : high;
  return Math.max(1, Math.min(LATER_AMOUNT, (nextLow + nextHigh) / 2));
}

export function summarizeCurve(answers: AnsweredTrial[]): CurvePoint[] {
  const grouped = new Map<number, AnsweredTrial[]>();
  for (const answer of answers) {
    const group = grouped.get(answer.delayDays) ?? [];
    group.push(answer);
    grouped.set(answer.delayDays, group);
  }

  return DELAYS.map((delayDays) => {
    const group = grouped.get(delayDays) ?? [];
    if (!group.length) {
      return { delayDays, subjectiveValue: LATER_AMOUNT, normalizedValue: 1, kEstimate: 0, n: 0 };
    }
    const subjectiveValue = group.reduce((sum, answer) => sum + answer.subjectiveEstimate, 0) / group.length;
    const normalizedValue = Math.max(0.01, Math.min(1, subjectiveValue / LATER_AMOUNT));
    const kEstimate = delayDays === 0 ? 0 : Math.max(0.000001, (1 / normalizedValue - 1) / delayDays);
    return { delayDays, subjectiveValue, normalizedValue, kEstimate, n: group.length };
  });
}

function median(values: number[]) {
  const sorted = [...values].filter(Number.isFinite).sort((a, b) => a - b);
  if (!sorted.length) return 0;
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
}

function trapezoidAuc(points: CurvePoint[]) {
  const maxDelay = Math.max(...points.map((p) => p.delayDays), 365);
  const augmented = [{ delayDays: 0, normalizedValue: 1 }, ...points.map((p) => ({ delayDays: p.delayDays, normalizedValue: p.normalizedValue }))]
    .sort((a, b) => a.delayDays - b.delayDays);
  let area = 0;
  for (let i = 1; i < augmented.length; i++) {
    const prev = augmented[i - 1];
    const next = augmented[i];
    const width = (next.delayDays - prev.delayDays) / maxDelay;
    area += width * ((prev.normalizedValue + next.normalizedValue) / 2);
  }
  return Math.max(0, Math.min(1, area));
}

function fittedValue(k: number, delayDays: number) {
  return 1 / (1 + k * delayDays);
}

function classify(k: number, auc: number) {
  if (k < 0.0025 && auc > 0.72) {
    return {
      classLabel: 'Long-horizon gardener',
      classTone: 'Your future rewards keep a lot of their glow.',
      oneLine: 'You tend to let later rewards remain psychologically real.'
    };
  }
  if (k < 0.009 && auc > 0.52) {
    return {
      classLabel: 'Steady negotiator',
      classTone: 'You bargain with time, but it rarely mugs you outright.',
      oneLine: 'You discount the future, yet your curve still has a long tail.'
    };
  }
  if (k < 0.026) {
    return {
      classLabel: 'Present-weather rider',
      classTone: 'The near future has weather; the far future has climate.',
      oneLine: 'Immediate rewards tug hard, but the delayed vault still has gravity.'
    };
  }
  return {
    classLabel: 'Spark chaser',
    classTone: 'Now is loud; later needs subtitles.',
    oneLine: 'Small immediate rewards often outshine larger delayed ones.'
  };
}

export function buildFingerprint(answers: AnsweredTrial[]): Fingerprint {
  const curve = summarizeCurve(answers);
  const kValues = curve.filter((p) => p.n > 0).map((p) => p.kEstimate);
  const k = Math.max(0.000001, median(kValues));
  const logK = Math.log10(k);
  const auc = trapezoidAuc(curve);
  const early = curve.find((p) => p.delayDays === 7)?.normalizedValue ?? 1;
  const long = curve.find((p) => p.delayDays === 365)?.normalizedValue ?? 1;
  const presentBias = Math.max(0, Math.min(1, (1 - early) / Math.max(0.001, 1 - long)));
  const rmse = Math.sqrt(
    curve.reduce((sum, point) => sum + Math.pow(point.normalizedValue - fittedValue(k, point.delayDays), 2), 0) / curve.length
  );
  const consistency = Math.max(0, Math.min(1, 1 - rmse));
  const label = classify(k, auc);

  return {
    k,
    logK,
    auc,
    presentBias,
    consistency,
    ...label,
    curve,
    traits: [
      {
        label: 'Patience index',
        value: `${Math.round(auc * 100)}/100`,
        detail: 'Area under your normalized discount curve. Higher means delayed rewards retained more value.'
      },
      {
        label: 'Discount rate k',
        value: k.toPrecision(3),
        detail: 'Mazur hyperbolic k from V = A / (1 + kD). Larger k means steeper discounting.'
      },
      {
        label: 'Now-gravity',
        value: `${Math.round(presentBias * 100)}%`,
        detail: 'How much of your total future discounting appears near the front of the curve.'
      },
      {
        label: 'Curve coherence',
        value: `${Math.round(consistency * 100)}%`,
        detail: 'How cleanly your choices line up with one smooth hyperbolic curve.'
      }
    ]
  };
}

export const DELAY_SET = DELAYS;
export const LATER_REWARD = LATER_AMOUNT;
